import CarteSvg from '@/assets/Offboarding/Carte.svg';
import ResultatSvg from '@/assets/Offboarding/Resultat.svg';
import OrdonnanceSvg from '@/assets/Offboarding/Ordonnance.svg';
import CarnetSvg from '@/assets/Offboarding/Carnet.svg';
import DocumentSvg from '@/assets/Offboarding/Document.svg';
import {
  useSelectedPatient,
  useSendPatientConfirmation,
  useUpdatePatient,
} from '@/hooks/patient.hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';

import { PiEnvelopeSimple } from 'react-icons/pi';

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/types/graph';

import { useSnackbar } from 'notistack';
import { useChecklist } from '@/hooks/questionnaire.hooks';
import { cn } from '@/lib/utils';
import { isValidEmail } from '@/util/email';
import { getRejectedFields, SAVE_ERROR_MESSAGE } from '@/util/apiErrors';
import {
  KnownChecklistFamily,
  resolveChecklistFamily,
  STATIC_CHECKLIST_ITEMS,
} from '@aldebaran/common/constants/checklist';

const FAMILY_SVG: Record<KnownChecklistFamily, { src: string; alt: string }> = {
  CARTE: { src: CarteSvg, alt: 'Carte' },
  CARNET: { src: CarnetSvg, alt: 'Carnet' },
  RESULTAT: { src: ResultatSvg, alt: 'Resultat' },
  ORDONNANCE: { src: OrdonnanceSvg, alt: 'Ordonnance' },
};

const CheckListIcon = ({ family, step }: { family: string; step: number }) => {
  const known = resolveChecklistFamily(family);
  const { src, alt } = known
    ? FAMILY_SVG[known]
    : { src: DocumentSvg, alt: 'Document' };

  return (
    <div
      className={cn(
        'w-7 h-7 flex items-center justify-center rounded-md',
        step % 4 === 0 && 'bg-[#E8F1FD]',
        step % 4 === 1 && 'bg-[#FFF9D5]',
        step % 4 === 2 && 'bg-[#F2F1FE]',
        step % 4 === 3 && 'bg-[#E5F7DA]',
      )}
    >
      <img src={src} alt={alt} />
    </div>
  );
};

export const PatientChecklist = () => {
  const [showModal, setShowModal] = useState(false);
  const { mutateAsync: sendPatientConfirmation } = useSendPatientConfirmation();
  const patient = useSelectedPatient();
  const professionalId = useSelector(
    (state: RootState) => state.consultation.professional?.id,
  );
  const _checklistItems = useChecklist();
  const [checklistItems, setChecklistItems] = useState(STATIC_CHECKLIST_ITEMS);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const questionnaireId = useSelector(
    (state: RootState) => state.questionnaireId,
  );

  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: updatePatient } = useUpdatePatient();

  useEffect(() => {
    if (_checklistItems?.length > 0) {
      setChecklistItems([...STATIC_CHECKLIST_ITEMS, ..._checklistItems]);
    }
  }, [_checklistItems]);

  const sendPatientConfirmationWithChecklist = async (
    patientId: string,
    questionnaireId: string,
    professionalId: number,
  ) => {
    const response = await sendPatientConfirmation({
      patientId: patientId,
      questionnaireId: questionnaireId,
      professionalId: professionalId,
      withChecklist: true,
    });

    if (response.data.emailSent) {
      enqueueSnackbar('🎉 Rappel envoyé !', {
        variant: 'success',
        autoHideDuration: 3000,
      });
    } else {
      enqueueSnackbar("L'envoi du rappel a échoué", {
        variant: 'warning',
        autoHideDuration: 3000,
      });
    }
  };

  const handleSendReminderWithNewEmail = async () => {
    if (patient.data) {
      if (!isValidEmail(email)) {
        setEmailError('Adresse email non valide');
        return;
      }

      try {
        await updatePatient({
          id: patient.data.id,
          email: email,
        });
      } catch (error) {
        console.error(
          "Erreur lors de la mise à jour de l'adresse e-mail:",
          error,
        );
        setEmailError(
          getRejectedFields(error).includes('email')
            ? 'Adresse email non valide'
            : SAVE_ERROR_MESSAGE,
        );
        return;
      }
      if (questionnaireId && professionalId) {
        setShowModal(false);
        setEmailError('');
        sendPatientConfirmationWithChecklist(
          patient.data.id,
          questionnaireId,
          professionalId,
        );
      }
    }
  };

  const handleSendChecklistReminder = () => {
    if (!patient.data?.email || !isValidEmail(patient.data.email)) {
      setShowModal(true);
    } else if (questionnaireId && patient.data && professionalId) {
      sendPatientConfirmationWithChecklist(
        patient.data.id,
        questionnaireId,
        professionalId,
      );
    }
  };

  return (
    <>
      <div className="flex flex-col justify-start mt-4 z-50 px-4 n2xs:px-6 nsm:px-0">
        <div className="flex flex-col items-center bg-white/60 rounded-2xl p-4">
          <p className="mb-2 w-full text-start font-medium">
            Vos documents à apporter :
          </p>
          <div className="flex flex-col gap-4 w-full px-3 my-2 text-left text-sm">
            {checklistItems.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckListIcon family={item.family} step={i} />
                <p>{item.value}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2 mt-4">
            <Button
              variant="offboarding"
              size="offboarding"
              onClick={handleSendChecklistReminder}
            >
              <PiEnvelopeSimple className="size-6" />
              M&apos;envoyer un rappel
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <div className="h-full w-full flex flex-col items-center justify-center my-6">
            <p className="text-xl font-medium mb-4 text-center px-2">
              Recevoir un rappel de <br /> mes documents à apporter
            </p>
            <div
              className={`rounded-lg mb-2 ${
                emailError ? 'px-1 py-2 bg-red-100' : 'bg-white'
              }`}
            >
              <div className="relative w-80 nsm:w-88">
                <PiEnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-500" />
                <Input
                  placeholder="Votre adresse email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSendReminderWithNewEmail();
                    }
                  }}
                  type="email"
                  className="pl-10"
                />
              </div>
              {emailError ? (
                <p className="text-start text-base font-normal text-red-600 ml-1 py-0 mt-1.5">
                  {emailError}
                </p>
              ) : (
                <></>
              )}
            </div>
            <div className="flex items-center justify-center gap-2 nsm:gap-8 py-2 w-full ">
              <Button
                variant="ctaSecondary"
                size="xs"
                onClick={() => setShowModal(false)}
                className="text-gray-500"
              >
                Annuler
              </Button>
              <Button
                variant="ctaPrimary"
                size="xs"
                onClick={handleSendReminderWithNewEmail}
              >
                Envoyer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
